import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, Save, ShieldCheck, ShieldAlert, Info, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { updateCircuitBreaker } from '@/lib/edge-functions'
import { stepGeneratesMessage } from '@/lib/cadence-step-predicates'

// One row of step_type_circuit_breaker (mig 107/108) — the per-(cadence, step)
// anti-loop guard Carlos checks before scoring. Only the CONFIGURABLE thresholds
// are editable here; `status` is runtime state shown read-only.
export interface CircuitBreakerRow {
  cadence_id: string
  step_type: string
  status: string
  max_regen_rate: number
  max_reject_rate: number
  window_size: number
  window_hours: number
  min_samples_to_trip: number
  // Set on rows SYNTHESIZED from a real message step that has no persisted
  // breaker yet — never saved to the DB. `false`/absent = an existing row.
  isNew?: boolean
}

// Neutral seeds for a synthetic (not-yet-saved) breaker row. These mirror the
// step_type_circuit_breaker column defaults (mig 107 window_size=50,
// max_regen_rate=0.5, max_reject_rate=0.3; mig 108 min_samples_to_trip=20,
// window_hours=24), so saving a synthetic row lands on the same values the DB
// would have seeded anyway.
const BREAKER_DEFAULTS = {
  max_regen_rate: 0.5,
  max_reject_rate: 0.3,
  window_size: 50,
  window_hours: 24,
  min_samples_to_trip: 20,
} as const

// A cadence step (subset) — the shape we need to derive which steps have a fuse.
export interface BreakerStep {
  step_type: string
  config_json?: Record<string, unknown> | null
}

// Which steps have a circuit-breaker fuse? The same steps that generate an AI
// message (and thus run through Carlos' supervisor, which owns the breaker).
// Imported from the shared predicate so this can never drift from the runtime
// gate in process-queue (index.ts:1736-1737) — linkedin_connect only counts
// when config_json.send_note === true; a plain cold invite sends no message.

// Build a SYNTHETIC (not-yet-saved) breaker row for a step_type, seeded from the
// table defaults. `status='closed'` = the healthy resting state a fresh row gets.
function synthRow(cadenceId: string, stepType: string): CircuitBreakerRow {
  return {
    cadence_id: cadenceId,
    step_type: stepType,
    status: 'closed',
    ...BREAKER_DEFAULTS,
    isNew: true,
  }
}

const STEP_LABELS: Record<string, string> = {
  linkedin_connect: 'Invitación LinkedIn',
  send_email: 'Email',
  email_reply: 'Reply en el mismo hilo',
  linkedin_message: 'DM de LinkedIn',
  task: 'Tarea',
  linkedin_comment: 'Comentario LinkedIn',
  linkedin_like: 'Like LinkedIn',
}

function stepLabel(stepType: string): string {
  return STEP_LABELS[stepType] || stepType
}

// The status column is runtime state; map it to a friendly badge. Values come
// from the CHECK constraint: closed / open / tripped.
function StatusBadge({ status }: { status: string }) {
  if (status === 'tripped') {
    return (
      <Badge variant="destructive" className="shrink-0 gap-1">
        <ShieldAlert className="h-3 w-3" />
        Cortado (tripped)
      </Badge>
    )
  }
  if (status === 'open') {
    return (
      <Badge variant="secondary" className="shrink-0 gap-1">
        <ShieldCheck className="h-3 w-3" />
        Abierto (con tráfico)
      </Badge>
    )
  }
  // 'closed' (healthy, supervisor active) or any unexpected value.
  return (
    <Badge variant="outline" className="shrink-0 gap-1 border-green-500/40 text-green-700 dark:text-green-400">
      <ShieldCheck className="h-3 w-3" />
      Sano (closed)
    </Badge>
  )
}

interface EditState {
  max_regen_rate: string
  max_reject_rate: string
  window_size: string
  window_hours: string
  min_samples_to_trip: string
}

function toEdit(row: CircuitBreakerRow): EditState {
  return {
    max_regen_rate: String(row.max_regen_rate),
    max_reject_rate: String(row.max_reject_rate),
    window_size: String(row.window_size),
    window_hours: String(row.window_hours),
    min_samples_to_trip: String(row.min_samples_to_trip),
  }
}

function CircuitBreakerRowCard({
  row,
  orgId,
  onSaved,
}: {
  row: CircuitBreakerRow
  orgId: string
  onSaved: () => void
}) {
  const [edit, setEdit] = useState<EditState>(() => toEdit(row))

  // A synthetic (isNew) row has no persisted counterpart, so it's always
  // saveable even at defaults; an existing row is saveable only once edited.
  const dirty =
    row.isNew ||
    edit.max_regen_rate !== String(row.max_regen_rate) ||
    edit.max_reject_rate !== String(row.max_reject_rate) ||
    edit.window_size !== String(row.window_size) ||
    edit.window_hours !== String(row.window_hours) ||
    edit.min_samples_to_trip !== String(row.min_samples_to_trip)

  const save = useMutation({
    mutationFn: async () => {
      const maxRegen = Number(edit.max_regen_rate)
      const maxReject = Number(edit.max_reject_rate)
      const windowSize = Number(edit.window_size)
      const windowHours = Number(edit.window_hours)
      const minSamples = Number(edit.min_samples_to_trip)

      // Client-side range guard (the edge fn re-validates authoritatively).
      if (Number.isNaN(maxRegen) || maxRegen < 0 || maxRegen > 1) {
        throw new Error('La tasa máx. de regeneración debe estar entre 0 y 1')
      }
      if (Number.isNaN(maxReject) || maxReject < 0 || maxReject > 1) {
        throw new Error('La tasa máx. de rechazo debe estar entre 0 y 1')
      }
      if (!Number.isInteger(windowSize) || windowSize < 1 || windowSize > 10000) {
        throw new Error('El tamaño de ventana debe ser un entero entre 1 y 10000')
      }
      if (!Number.isInteger(windowHours) || windowHours < 1 || windowHours > 720) {
        throw new Error('Las horas de ventana deben ser un entero entre 1 y 720')
      }
      if (!Number.isInteger(minSamples) || minSamples < 1 || minSamples > 10000) {
        throw new Error('Las muestras mínimas deben ser un entero entre 1 y 10000')
      }
      return updateCircuitBreaker({
        orgId,
        cadence_id: row.cadence_id,
        step_type: row.step_type,
        max_regen_rate: maxRegen,
        max_reject_rate: maxReject,
        window_size: windowSize,
        window_hours: windowHours,
        min_samples_to_trip: minSamples,
      })
    },
    onSuccess: () => {
      toast.success(`Circuit breaker guardado — ${stepLabel(row.step_type)}`)
      onSaved()
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'No se pudo guardar el circuit breaker')
    },
  })

  return (
    <div className="rounded-lg border bg-card">
      {/* Row header */}
      <div className="flex flex-wrap items-center gap-3 p-3 border-b">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate flex items-center gap-1.5">
            {stepLabel(row.step_type)}
            {row.isNew ? (
              <Badge variant="secondary" className="gap-1 font-normal">
                <Sparkles className="h-3 w-3" /> Nuevo
              </Badge>
            ) : (
              <Badge variant="outline" className="font-normal">Propio</Badge>
            )}
          </p>
          <p className="text-xs text-muted-foreground font-mono">{row.step_type}</p>
        </div>
        {!row.isNew && <StatusBadge status={row.status} />}
        <Button
          size="sm"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending
            ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            : <Save className="h-3.5 w-3.5 mr-1.5" />}
          Guardar
        </Button>
      </div>

      {/* Threshold editors */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 p-3">
        <div className="space-y-1">
          <Label className="text-xs">Tasa máx. regeneración</Label>
          <Input
            type="number"
            step="0.05"
            min={0}
            max={1}
            value={edit.max_regen_rate}
            onChange={(e) => setEdit((s) => ({ ...s, max_regen_rate: e.target.value }))}
            className="h-8"
          />
          <p className="text-[11px] text-muted-foreground leading-tight">
            Fracción (0–1) de mensajes que Carlos manda regenerar antes de cortar el step. 0.5 = 50%.
          </p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Tasa máx. rechazo</Label>
          <Input
            type="number"
            step="0.05"
            min={0}
            max={1}
            value={edit.max_reject_rate}
            onChange={(e) => setEdit((s) => ({ ...s, max_reject_rate: e.target.value }))}
            className="h-8"
          />
          <p className="text-[11px] text-muted-foreground leading-tight">
            Fracción (0–1) de mensajes rechazados antes de cortar el step. 0.3 = 30%.
          </p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Tamaño de ventana</Label>
          <Input
            type="number"
            step="1"
            min={1}
            max={10000}
            value={edit.window_size}
            onChange={(e) => setEdit((s) => ({ ...s, window_size: e.target.value }))}
            className="h-8"
          />
          <p className="text-[11px] text-muted-foreground leading-tight">
            Cuántos mensajes recientes se miran para calcular esas tasas.
          </p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Horas de ventana</Label>
          <Input
            type="number"
            step="1"
            min={1}
            max={720}
            value={edit.window_hours}
            onChange={(e) => setEdit((s) => ({ ...s, window_hours: e.target.value }))}
            className="h-8"
          />
          <p className="text-[11px] text-muted-foreground leading-tight">
            Cada cuántas horas se reinicia la ventana (los contadores vuelven a cero).
          </p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Muestras mínimas</Label>
          <Input
            type="number"
            step="1"
            min={1}
            max={10000}
            value={edit.min_samples_to_trip}
            onChange={(e) => setEdit((s) => ({ ...s, min_samples_to_trip: e.target.value }))}
            className="h-8"
          />
          <p className="text-[11px] text-muted-foreground leading-tight">
            Cuántos mensajes debe haber en la ventana antes de poder cortar (evita cortar con pocos datos).
          </p>
        </div>
      </div>
    </div>
  )
}

export function CircuitBreakerTable({
  rows,
  steps,
  orgId,
  cadenceId,
}: {
  rows: CircuitBreakerRow[]
  // The REAL steps of this cadence — used to synthesize an empty breaker per
  // message step that has no persisted row yet. Defaults to [] for callers that
  // don't pass them (falls back to showing only existing rows).
  steps?: BreakerStep[]
  orgId: string
  cadenceId: string
}) {
  const queryClient = useQueryClient()
  const onSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['circuit-breakers', orgId, cadenceId] })
  }

  // Merge existing rows (priority) with synthetic ones for every REAL message
  // step of the cadence that has no persisted breaker yet. Deduped by step_type
  // (the breaker key is (org, cadence, step_type) — no day_offset), mirroring
  // how PolicySection/AnglesSection seed empty rows from the real steps so the
  // tab is never blank when the cadence has message steps.
  const mergedRows = useMemo<CircuitBreakerRow[]>(() => {
    const byStepType = new Map<string, CircuitBreakerRow>()
    for (const r of rows) byStepType.set(r.step_type, r) // existing wins

    const synthStepTypes = new Set<string>()
    for (const s of steps ?? []) {
      if (stepGeneratesMessage(s)) synthStepTypes.add(s.step_type)
    }
    for (const stepType of synthStepTypes) {
      if (!byStepType.has(stepType)) byStepType.set(stepType, synthRow(cadenceId, stepType))
    }

    // Existing rows first (in their given order), then synthetic ones sorted by
    // label for stability.
    const existing = rows.slice()
    const synthetic = [...byStepType.values()]
      .filter((r) => r.isNew)
      .sort((a, b) => stepLabel(a.step_type).localeCompare(stepLabel(b.step_type)))
    return [...existing, ...synthetic]
  }, [rows, steps, cadenceId])

  if (mergedRows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
        Esta cadencia todavía no tiene circuit breakers. Se sembrarán en cuanto la cadencia tenga
        pasos de mensaje (email, DM, invitación con nota, comentario…) en el tab Steps.
      </div>
    )
  }

  const hasSynthetic = mergedRows.some((r) => r.isNew)

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-muted bg-muted/30 p-3 flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>
          El circuit breaker es un fusible por step: si en la ventana la tasa de regeneración o de
          rechazo supera su umbral (una vez que hay al menos "muestras mínimas"), Carlos <strong>corta
          ese step</strong> y deja de gastar dinero regenerando un mensaje que no mejora. El estado
          (<em>closed</em> = sano, <em>open</em> = con tráfico, <em>tripped</em> = cortado) es
          automático y solo de lectura; aquí ajustas los umbrales que deciden cuándo corta.
        </span>
      </div>
      {hasSynthetic && (
        <div className="rounded-md border border-muted bg-muted/30 p-3 flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Las filas marcadas <strong>Nuevo</strong> aún no existen en la base: se derivan de los
            pasos de mensaje reales de esta cadencia y muestran los valores por defecto. Guarda una
            para fijar sus umbrales para esta cadencia (hasta entonces ese paso corre sin fusible
            explícito y usa los defaults).
          </span>
        </div>
      )}
      {mergedRows.map((row) => (
        <CircuitBreakerRowCard
          key={`${row.cadence_id}:${row.step_type}`}
          row={row}
          orgId={orgId}
          onSaved={onSaved}
        />
      ))}
    </div>
  )
}
